import { InjectionToken } from '@angular/core';
import { MatomoModuleConfiguration } from '@redngapps/shared/tracking/types';
import { IICEServers, StringWithReplacement, UrlString, UrlStringWithLocalhost } from '@redngapps/shared/types';

import { EEnvironment } from './environment.enum';

export const ENVIRONMENT = new InjectionToken<IEnvironment>('ENVIRONMENT');

/**
 * ! When `featureSwitches` properties are renamed, make sure to also rename FeatureSwitchService calls
 *  (e.g. `featureSwitchService.isAvailable('sentry')`) !
 */
export interface IEnvironment {
  name: EEnvironment;
  urls: {
    cms?: UrlStringWithLocalhost | StringWithReplacement<'origin', '/cms/api'>;
    licenseSelect?: string;
    help?: string;
    /**
     * Base-url for the RED-Videosprechstunde.
     *
     * This will be overwritten by a team-city-task for `testserver`, `staging` and `integration` environments.
     * Since the url for those environments is based on ticket-numbers.
     */
    videoAppointment?: string;
    impress?: string;
    privacyNotice?: string;
    /**
     * URL pointing to the Pruefmodule API
     */
    pruefmodulServer?: UrlString<'https'> | StringWithReplacement<'origin', '/pruefmodule'>;
    /**
     * URL pointing to the holidays API
     */
    holidaysServer?: UrlString<'https'> | StringWithReplacement<'origin'>;
    /**
     * Base URL of this instance's feature-flag service (bulk-read route `GET /public/flags`).
     * When unset, the runtime feature-flag layer stays inert (no override, falls back to
     * license/env as before).
     */
    // A bare relative path (e.g. '/feature-flags') is allowed for local development, where it is
    // routed to the service through the dev proxy (apps/client/proxy.conf.js). Deployed environments
    // use the https / {{origin}} form.
    featureFlagsService?: UrlString<'https'> | StringWithReplacement<'origin', '/feature-flags'> | '/feature-flags';
    /**
     * The --epa-as url parameter to pass to the telematik api.
     * It can point to a dev, ref, or prod environment.
     */
    epa?: string;
    etssFachdienst?: string;
    etssDiscovery?: string;
    signalingServer?: string;
    signalingApi?: string;
    redClient?: string;
    turnServer?: IICEServers;
    redAgent?: {
      version: string;
      download: {
        mac: string;
        windows: string;
      };
      wannst?: string;
    };
  };
  /**
   * These flags must be kept without reference to the FeatureIdentifier-Interface since we cannot use it in the app module declarations otherwise.
   */
  featureSwitches: {
    /**
     * TODO: remove this flag with DEV-20020
     */
    akteAttachmentsMail?: boolean;
    /**
     * Determines if, ```enableProdMode()``` will be called in the main.ts
     */
    angularProdModeEnabled: boolean;
    /**
     * Determines if Application Performance Monitoring (APM) is enabled
     */
    apm: boolean;
    datalossWarningInDatenerfassung?: boolean;
    /**
     * TODO: remove this flag with DEV-19682
     */
    deactivateDoctor?: boolean;
    /**
     * TODO: remove this flag with DEV-20155
     */
    eigeneRegelnAbrechnung?: boolean;
    /**
     * TODO: remove this flag with DEV-20155
     */
    eigeneRegelnAuswertung?: boolean;
    /**
     * Enables periodic polling of the runtime feature-flag service (triggered on navigation).
     * Off where no feature-flags service is configured.
     */
    featureFlagsPolling?: boolean;
    /**
     * TODO: remove this flag with DEV-19299
     */
    heilmittelverordnung?: boolean;
    /**
     * TODO: remove this flag with DEV-19833
     */
    locations?: boolean;
    mailversandTerminerinnerung?: boolean;
    muster12?: boolean;
    muster12Blanko?: boolean;
    /**
     * TODO: remove this flag with DEV-19300
     */
    muster13Blanko?: boolean;
    /**
     * TODO: remove this flag with DEV-20743
     */
    muster26?: boolean;
    /**
     * TODO: remove this flag with DEV-20743
     */
    muster26Blanko?: boolean;
    /**
     * TODO: remove this flag with DEV-20957
     */
    muster27?: boolean;
    /**
     * TODO: remove this flag with DEV-20957
     */
    muster27Blanko?: boolean;
    /**
     * TODO: remove this flag with DEV-20476
     */
    muster7Blanko?: boolean;
    sentry: boolean;
    apo?: boolean;
    basis?: boolean;
    bdtImport?: boolean;
    chipcardSimulatorAvailable?: boolean;
    connect?: boolean;
    connectTokenApi?: boolean;
    devTools?: boolean;
    /**
     * Determines if eAus are sent to TI Referenzumgebung (true) or Produktivumgebung (false)
     */
    isEauTestEnvironment?: boolean;
    isEauZertiMode?: boolean;
    krw?: boolean;
    mailClientDienstkennungen?: boolean;
    /**
     * Measures time for each RedServicePromised request and logs a warning, if the request takes more than 1 second.
     */
    monitorSlowRedServiceRequests?: boolean;
    newEpisodenmanager?: boolean;
    pharma?: boolean;
    isActiveB1Endpoint?: boolean;
    preProcessVermittlungscode?: boolean;
    /** if set to 'true' a mock service PtvMockService will be used instead of PtvService (always resolves a static example PDF). */
    ptvMock?: boolean;
    /**
     * If set to `true` "info@redmedical.de" will be add to bcc of mails.
     *
     * *This should only be activated for production. Since it's used for statistics by the GF.*
     */
    sendBccToInfoMail?: boolean;
    /**
     * Determines if a e-mails will be send.
     *
     * *If set to `false`, the mails should be logged to the console.*
     */
    sendEmail?: boolean;
    /**
     * if true then show fake data selection KV Abrechnung
     */
    showFakeDataKVAbrechnung?: boolean;
    softwareUpdate?: boolean;
    /** if set to `true` `storeFreeze` reducer from `ngrx-store-freeze` is added to the `metaReducers` */
    storeFreeze?: boolean;
    strapiBroadcast?: boolean;
    /**
     * TODO: remove this flag with DEV-19987
     */
    terminbestaetigung?: boolean;
    /**
     * TODO: remove this flag with DEV-20848
     */
    tiKonfiguration?: boolean;
    /**
     * TODO: remove this flag with DEV-20034
     */
    umsatzsteuer?: boolean;
    useCustomErrorHandler?: boolean;
    useFakeDates?: boolean;
    /**
     * TODO: remove this flag with DEV-16864
     */
    terminkalenderV3?: boolean;
    /**
     * Determines if webcomponents from 'redmedical/red-components' are loaded via HTTPS (true) or via local NPM files copied to /assets (false | undefined)
     */
    webComponentsOverHTTP?: boolean;
    gruppentherapieDokumentation?: boolean;
    importErrorModule?: boolean;
  };

  ti?: {
    /**
     * The client id we received from the gematik to access TI services such as the ePA or erezept.
     * There are different client ids for different ti environments (Referenzumgebung and Produktivumgebung).
     */
    clientId: string;
  };

  matomo?: MatomoModuleConfiguration;

  softwareVersion: string;
  /**
   * The git-hash of the last commit which will be replaced by the build-server. Updated via git-hash-update script.
   */
  gitHash: string;
}
